import type { ThemeSelectedFonts } from '@sites/site-theme/src/types/theme-font.type';

import { FONT_FAMILIES } from '@sites/site-theme/src/config/fonts.const';

/**
 * The distinct Google-Fonts family names a theme's tags use.
 *
 * Iterates the array rather than `Object.keys` of it — `selected` is a `ThemeFont[]`, so the old
 * form indexed an array by a STRING key and typed both the key and the lookup as `any`. It worked,
 * and it stopped compiling the moment this file became reachable from a project with
 * `noImplicitAny` (`apps/mcp-server`, once the theme package started exporting it for the MCP write
 * path).
 */
function formatFontsNames(fonts: ThemeSelectedFonts | undefined): string[] {
    const uniqueFontNames = new Set<string>();

    (fonts?.selected ?? []).forEach((font) => {
        const fontName = FONT_FAMILIES[font.font]?.name;

        if (fontName) {
            uniqueFontNames.add(fontName);
        }
    });

    return [...uniqueFontNames];
}

function formatFontsLink(fonts: ThemeSelectedFonts | undefined): string {
    return formatFontsNames(fonts)
        .map((fontName: string) => {
            const name = fontName.replace(' ', '+');
            return `family=${name}:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&`;
        })
        .join('');
}

/**
 * `fonts` is optional because a theme's font selection is: every part of `websiteThemePropsSchema`
 * may be absent on a page saved before its panels were opened. The body has always guarded for it
 * (`fonts?.selected`); the signature just said otherwise, which only surfaced when a caller under
 * `strictNullChecks` passed a theme read straight off a page.
 */
export function formatFontsLinkAsImport(fonts: ThemeSelectedFonts | undefined): string {
    return `@import url('https://fonts.googleapis.com/css2?${formatFontsLink(fonts)}display=swap');\n`;
}

export function formatFontsLinkAsLink(fonts: ThemeSelectedFonts | undefined): string {
    return `https://fonts.googleapis.com/css2?${formatFontsLink(fonts)}display=swap`;
}
